import { useMemo, useState } from "react";

import type { PricingData, PricingModel } from "@/landing/lib/pricing-api";
import {
  dedupePrice,
  formatPrice,
  formatUnit,
  getCategory,
} from "@/landing/lib/pricing-api";
import { JoinBand, PageHero } from "@/landing/system/blocks";
import { HeroCat } from "@/landing/system/cat";
import { FilterPills } from "@/landing/system/controls";
import { Eyebrow, Section, SectionHeading } from "@/landing/system/layout";

const GROUPS = [
  { id: "image", label: "Image" },
  { id: "video", label: "Video" },
  { id: "audio", label: "Audio" },
  { id: "scraping", label: "Scraping" },
] as const;

type GroupId = (typeof GROUPS)[number]["id"];
type Filter = "all" | GroupId;

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  ...GROUPS.map((group) => ({ value: group.id, label: group.label })),
];

/**
 * /models, as minimal as the homepage: the hero, then every model's name and
 * per-use price, grouped and filterable. Out of the nav.
 */
export function CliModelPricing({ data }: { data: PricingData }) {
  const [filter, setFilter] = useState<Filter>("all");

  const grouped = useMemo(() => {
    const groups: Record<string, PricingModel[]> = {};
    for (const model of data.models) {
      const category = getCategory(model.stepType);
      (groups[category] ??= []).push(model);
    }
    for (const models of Object.values(groups)) {
      models.sort((left, right) => left.label.localeCompare(right.label));
    }
    return groups;
  }, [data.models]);

  const visible = GROUPS.filter(
    (group) => filter === "all" || filter === group.id,
  );

  return (
    <>
      <PageHero
        eyebrow="Models"
        title="Generation, at cost."
        sub="Image, video and audio models for your agent, billed per use with no markup."
        visual={<HeroCat />}
      />

      <Section>
        <SectionHeading eyebrow="Prices" title="Every model, per use." />
        <FilterPills
          className="-mx-(--g-margin) mt-12 px-(--g-margin) md:mx-0 md:px-0"
          label="Model category"
          options={FILTERS}
          value={filter}
          onChange={setFilter}
        />

        <div className="mt-10 flex flex-col gap-14">
          {visible.map((group) => {
            const models = grouped[group.id] ?? [];
            if (!models.length) return null;
            return (
              <section key={group.id} aria-labelledby={`models-${group.id}`}>
                <div className="mb-4 flex items-baseline justify-between gap-6">
                  <h2
                    id={`models-${group.id}`}
                    className="m-0 text-[21px] font-medium tracking-[-0.02em] text-(--g-ink)"
                  >
                    {group.label}
                  </h2>
                  <Eyebrow>
                    {models.length} {models.length === 1 ? "model" : "models"}
                  </Eyebrow>
                </div>
                <ul className="m-0 list-none border-b border-(--g-rule) p-0">
                  {models.map((model) => (
                    <ModelRow key={model.key} model={model} />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </Section>

      <JoinBand />
    </>
  );
}

/** "1k" and "1K" read as one label; "basic" becomes "Basic"; "Auto" is the max tier. */
function variantLabel(variant: string): string {
  if (variant === "Auto") return "Max";
  if (/^\d+k$/i.test(variant)) return variant.toUpperCase();
  return variant.charAt(0).toUpperCase() + variant.slice(1);
}

/** A model's name and its prices on one line, the unit said once. */
function ModelRow({ model }: { model: PricingModel }) {
  const tiers = dedupePrice(model.pricing);
  const units = new Set(tiers.map((tier) => tier.unit));
  const sharedUnit = units.size === 1 ? tiers[0]?.unit : undefined;

  return (
    <li className="grid gap-1.5 border-t border-(--g-rule) py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-baseline sm:gap-8">
      <h3 className="m-0 text-[16px] font-medium tracking-[-0.01em] text-(--g-ink)">
        {model.label}
      </h3>
      <p className="m-0 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[15px] text-(--g-ink-2) sm:justify-end">
        {tiers.map((tier) => (
          <span
            key={`${tier.variant ?? ""}:${tier.unit}:${tier.unitPriceMicrodollars}`}
            className="inline-flex items-baseline gap-1.5"
          >
            {tiers.length > 1 && tier.variant && (
              <span className="font-mono text-[12px] text-(--g-ink-3)">
                {variantLabel(tier.variant)}
              </span>
            )}
            <span className="text-(--g-ink) tabular-nums">
              {formatPrice(tier.unitPriceMicrodollars)}
            </span>
            {!sharedUnit && (
              <span className="text-[13px] text-(--g-ink-3)">
                {formatUnit(tier.unit)}
              </span>
            )}
          </span>
        ))}
        {sharedUnit && (
          <span className="text-[13px] text-(--g-ink-3)">
            {formatUnit(sharedUnit)}
          </span>
        )}
      </p>
    </li>
  );
}
